import React, { useCallback, useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { makeStyles } from 'tss-react/mui';
import { hideNotification } from '../../actions';
import { areThereNotifications } from '../../functions';
import { emptyWebNoticeStack, foldWebNotices } from '../../webNoticeStack';
import NotificationsTransition from '../NotificationsTransition';
import Notification from './Notification';
// In a call: top centre, under the call header, never on the call controls
// and never over the middle of the video (notifications mockups, 2026-11-02).
const useStyles = makeStyles()(() => {
    return {
        container: {
            alignItems: 'center',
            display: 'flex',
            flexDirection: 'column',
            left: '50%',
            maxWidth: 'calc(100% - 24px)',
            pointerEvents: 'none',
            position: 'absolute',
            top: 'calc(env(safe-area-inset-top, 0px) + 72px)',
            transform: 'translateX(-50%)',
            width: '420px',
            zIndex: 600
        },
        containerPortal: {
            maxWidth: 'calc(100% - 24px)',
            top: 'calc(env(safe-area-inset-top, 0px) + 56px)'
        }
    };
});
const NotificationsContainer = ({ _iAmSipGateway, _notifications, dispatch, portal }) => {
    const { classes, cx } = useStyles();
    const stack = useRef(emptyWebNoticeStack());
    const [visibleUid, setVisibleUid] = useState(null);
    // While the pointer or the keyboard focus is on a notice, its timer
    // stops: the notice stays on screen until the pointer or focus leaves,
    // even when its redux timer has run out.
    const [held, setHeld] = useState(null);
    const holding = useRef(false);
    const _onDismissed = useCallback((uid) => {
        holding.current = false;
        setHeld(null);
        dispatch(hideNotification(uid));
    }, []);
    useEffect(() => {
        const result = foldWebNotices(stack.current, _notifications.map(({ props, uid }) => {
            return {
                appearance: props.appearance,
                hasAction: Boolean(props.customActionNameKey?.length && props.customActionHandler?.length),
                uid
            };
        }), Date.now());
        stack.current = result.stack;
        setVisibleUid(result.visible);
        result.hide.forEach(uid => dispatch(hideNotification(uid)));
    }, [_notifications]);
    const current = _notifications.find(({ uid }) => uid === visibleUid) || null;
    useEffect(() => {
        if (holding.current && current)
            setHeld(current);
    }, [current]);
    const hold = useCallback(() => {
        holding.current = true;
        setHeld(current);
    }, [current]);
    const release = useCallback((event) => {
        if (event?.relatedTarget && event.currentTarget.contains(event.relatedTarget))
            return;
        holding.current = false;
        setHeld(null);
    }, []);
    if (_iAmSipGateway) {
        return null;
    }
    const shown = current || (held && !_notifications.some(({ uid }) => uid === held.uid) ? held : null);
    return (React.createElement("div", { className: cx(classes.container, {
            [classes.containerPortal]: portal
        }), id: 'notifications-container', onBlur: release, onFocus: hold, onPointerEnter: hold, onPointerLeave: () => release() },
        React.createElement(NotificationsTransition, null, shown ? [
            React.createElement(Notification, { ...shown.props, key: shown.uid, onDismissed: _onDismissed, uid: shown.uid })
        ] : [])));
};
/**
 * Maps (parts of) the Redux state to the associated props for this component.
 *
 * @param {Object} state - The Redux state.
 * @private
 * @returns {IProps}
 */
function _mapStateToProps(state) {
    const { notifications } = state['features/notifications'];
    const { iAmSipGateway } = state['features/base/config'];
    const { isOpen: isChatOpen } = state['features/chat'];
    const _visible = areThereNotifications(state);
    return {
        _iAmSipGateway: Boolean(iAmSipGateway),
        _isChatOpen: isChatOpen,
        _notifications: _visible ? notifications : []
    };
}
export default connect(_mapStateToProps)(NotificationsContainer);
